import React, {useContext, useEffect, useState} from "react";
import {AppContextContext, ExternalSearchPricingServiceContext} from "../../Contexts";
import {Breadcrumb, Button, Descriptions, Form, InputNumber, message, Modal, Select, Skeleton, Spin} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import ExternalSearchPricing from "../../domain/ExternalSearchPricing";
import {ExternalWebSearchBackend, formatExternalWebSearchBackend} from "../../domain/AiExtractor";
import {useForm} from "antd/es/form/Form";
import CreditsValue from "../common/CreditsValue";

function ExternalSearchPricingDetail() {
    const appContext = useContext(AppContextContext);
    const externalSearchPricingService = useContext(ExternalSearchPricingServiceContext);
    const navigate = useNavigate();
    const {externalSearchPricingId} = useParams<{ externalSearchPricingId: string }>();
    const [pricing, setPricing] = useState<ExternalSearchPricing | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [form] = useForm();
    const priceInputProps = {min: 0, max: 1000, step: 0.0001, stringMode: true, addonAfter: "credits", style: {width: "100%"}};

    const layout = {labelCol: {xs: {span: 24}, sm: {span: 8}}, wrapperCol: {xs: {span: 24}, sm: {span: 16}}};
    const tailLayout = {wrapperCol: {xs: {span: 24}, sm: {offset: 8, span: 16}}};
    const title = externalSearchPricingId ? (pricing?.backend ? formatExternalWebSearchBackend(pricing.backend) : "External search credits") : "New external search credits";

    useEffect(() => {
        if (externalSearchPricingId) {
            reload();
        } else {
            setEditMode(true);
        }
    }, [externalSearchPricingId]);

    if (externalSearchPricingId && !pricing) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/external-search-pricing"}>External search credits</Link>},
                                {title}
                            ]}
                />

                <Spin spinning={externalSearchPricingId ? !pricing : false} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{title}</h1>

                    {externalSearchPricingId &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button type={"primary"} onClick={() => setEditMode(!editMode)}>Edit</Button>
                            <Button
                                danger
                                style={{marginLeft: 8}}
                                onClick={() => Modal.confirm({
                                    content: "Do you really want to delete this external search credit definition?",
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    okButtonProps: {danger: true},
                                    onOk: () => onDelete()
                                })}
                            >
                                Delete
                            </Button>
                        </div>
                    }

                    <Collapse isOpen={!externalSearchPricingId || editMode}>
                        {externalSearchPricingId && <h3>Edit external search credits</h3>}

                        <Form
                            {...layout}
                            form={form}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${externalSearchPricingId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                        >
                            <Form.Item name="backend" label="Backend" rules={[{required: true, message: "Backend is required."}]}>
                                <Select
                                    options={Object.values(ExternalWebSearchBackend).map(value => ({
                                        value,
                                        label: formatExternalWebSearchBackend(value)
                                    }))}
                                />
                            </Form.Item>

                            <Form.Item name="pricePer1kCalls" label="Per 1K billable calls" rules={[{required: true, message: "Credits value is required."}]}>
                                <InputNumber {...priceInputProps}/>
                            </Form.Item>

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">Save</Button>
                                <Button
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        if (externalSearchPricingId) {
                                            setEditMode(false);
                                            if (pricing) {
                                                form.setFieldsValue(pricing);
                                            }
                                        } else {
                                            navigate("/definitions/external-search-pricing");
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {externalSearchPricingId && pricing &&
                        <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                            <Descriptions.Item label="Backend">{formatExternalWebSearchBackend(pricing.backend)}</Descriptions.Item>
                            <Descriptions.Item label="Per 1K billable calls"><CreditsValue value={pricing.pricePer1kCalls} fixedDecimals={4}/></Descriptions.Item>
                        </Descriptions>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!externalSearchPricingId) return;

        externalSearchPricingService.get(externalSearchPricingId)
            .then(value => {
                setPricing(value);
                form.setFieldsValue(value);
            });
    }

    function onFinish(values: any) {
        if (externalSearchPricingId) {
            externalSearchPricingService.update(externalSearchPricingId, values)
                .then(() => {
                    message.success("External search credits updated.");
                    setEditMode(false);
                    reload();
                });
        } else {
            externalSearchPricingService.add(values)
                .then(() => {
                    message.success("External search credits added.");
                    navigate("/definitions/external-search-pricing");
                });
        }
    }

    function onDelete() {
        if (!pricing) return;

        externalSearchPricingService.delete(pricing)
            .then(() => {
                message.success("External search credits deleted.");
                navigate("/definitions/external-search-pricing");
            });
    }
}

export default ExternalSearchPricingDetail;
